import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useRefresh } from "react-admin";
import sendRequest from "../../dataProvider/sendRequest";
import useNotify from "../../unit/useNotify";
import { set } from "lodash";
import HeaderLabelTitle from "../../unit/HeaderLabelTitle";
import CustomButtonCancel from "../../unit/CustomButtonCancel";

type RefillFormData = {
  initiateTransferDate: string;
  transferAmount: string;
  transferBankAccount: string;
};

const defaultFormData: RefillFormData = {
  initiateTransferDate: "",
  transferAmount: "",
  transferBankAccount: "",
};

const bankAccountOptions = [
  { value: "bank_account_1", label: "Bank Account 1" },
  { value: "bank_account_2", label: "Bank Account 2" },
  { value: "dwolla_balance", label: "Dwolla Balance" },
];

type BankAccountRow = {
  bankAccountId: string;
  bankAccountStatus?: string;
  sStatus?: string;
  sInstitutionName?: string;
  sAccountType?: string;
  sRoutingNumber?: string;
  sAccountNumber?: string;
};

const normalizeBankAccounts = (accountList: any): BankAccountRow[] => {
  return Object.values(accountList).map((item: any) => ({
    bankAccountId: String(item.bankAccountId),
    bankAccountStatus: item.bankAccountStatus,
    sStatus: item.sStatus,
    sInstitutionName: item.sInstitutionName,
    sAccountType: item.sAccountType,
    sRoutingNumber: item.sRoutingNumber,
    sAccountNumber: item.sAccountNumber,
  }));
};
const getBankAccountStatusText = (item: BankAccountRow) => {
  if (item.bankAccountStatus === "USED") {
    return `Used${item.sStatus ? ` (${item.sStatus})` : ""}`;
  }
  if (item.bankAccountStatus === "DISABLED") {
    return "Disabled";
  }
  if (item.bankAccountStatus === "ACTIVE") {
    return "Active";
  }
  return item.bankAccountStatus || "";
};

const isValidDateString = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime());
};

const Refill = ({ ...props }) => {
  const refresh = useRefresh();
  const { successNotify, errorNotify } = useNotify();
  const [loading, setLoading] = React.useState(false);
  const [transferBankAccount, setTransferBankAccount] = React.useState("");
  const [transferDate, setTransferDate] = React.useState("");
  const [transferAmount, setTransferAmount] = React.useState("");
  const [minDwollaBalance, setMinDwollaBalance] = React.useState("");
  const [errors, setErrors] = React.useState({
    transferDate: false,
    transferAmount: false,
    transferBankAccount: false,
  });
  const [bankAccountRows, setBankAccountRows] = React.useState<
    BankAccountRow[]
  >([]);
  // const [bankAccountId, setBankAccountId] = React.useState("");

  //页面加载时，默认transferDate为今天，获取bank account
  React.useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setTransferDate(today);
    //request refill/bank
    const fetchBankAccounts = async () => {
      const response = await sendRequest("refill/action/getBankAccount", {
        id: "0",
      });
      if (response && (response.code == "200" || response.code == 200)) {
        const bankAccountList = response?.bankAccountData || [];
        const normalizedAccounts = normalizeBankAccounts(bankAccountList);
        setBankAccountRows(normalizedAccounts);
        if (normalizedAccounts.length > 0) {
          setTransferBankAccount(normalizedAccounts[0].bankAccountId);
        }
      } else {
        errorNotify(response?.message || "Failed to fetch bank accounts");
      }
    };
    fetchBankAccounts();
  }, []);
  const handleSubmit = async () => {
    setLoading(true);
    const today = new Date().toISOString().split("T")[0];
    const transferDateInvalid =
      !isValidDateString(transferDate) || transferDate < today;
    const transferAmountInvalid =
      !transferAmount ||
      isNaN(Number(transferAmount)) ||
      Number(transferAmount) <= 0;
    const transferBankAccountInvalid = !transferBankAccount;

    setErrors({
      transferDate: transferDateInvalid,
      transferAmount: transferAmountInvalid,
      transferBankAccount: transferBankAccountInvalid,
    });

    //添加form validation
    if (transferDateInvalid) {
      errorNotify(
        "Please enter date as YYYY-MM-DD, and not earlier than today"
      );
      setLoading(false);
      return;
    }
    if (transferAmountInvalid) {
      errorNotify("Please enter a valid transfer amount");
      setLoading(false);
      return;
    }
    if (transferBankAccountInvalid) {
      errorNotify("Please select a bank account for transfer");
      setLoading(false);
      return;
    }
    const response = await sendRequest("refill/action/addPayment", {
      id: "new",
      data: {
        initiate_transfer_date: transferDate,
        amount: transferAmount,
        accountId: transferBankAccount,
        // minDwollaBalance: minDwollaBalance,
      },
    });

    if (response && (response.code == "200" || response.code == 200)) {
      successNotify("Refill submitted successfully");
      setTransferAmount("");
      setTransferDate(new Date().toISOString().split("T")[0]);
      setTransferBankAccount(
        bankAccountRows.length > 0 ? bankAccountRows[0].bankAccountId : ""
      );
      setMinDwollaBalance("");
      // refresh();
    } else {
      errorNotify(response?.message || "Refill submit failed");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          mt: "0px",
          backgroundColor: "#fff",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            padding: "20px",
            pt: 0,
          }}
        >
          <div style={{ marginTop: 16 }}>
            <HeaderLabelTitle
              title="Select Bank Account for Transfer"
              align="left"
            />
          </div>
          <Box
            sx={{
              mt: 1,
              border: errors.transferBankAccount
                ? "1px solid #d32f2f"
                : "1px solid transparent",
              borderRadius: "4px",
              px: 1,
            }}
          >
            {bankAccountRows.map((item) => (
              <Box
                key={item.bankAccountId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Checkbox
                  checked={transferBankAccount === item.bankAccountId}
                  onChange={() => {
                    setTransferBankAccount(item.bankAccountId);
                    if (errors.transferBankAccount) {
                      setErrors((prev) => ({
                        ...prev,
                        transferBankAccount: false,
                      }));
                    }
                  }}
                  value={item.bankAccountId}
                />
                <Box>
                  <Typography variant="body2">
                    <strong> [{getBankAccountStatusText(item)}]</strong>
                    &nbsp;&nbsp;
                    {item?.sInstitutionName} - {item?.sAccountType} ****
                    {item?.sAccountNumber?.slice(-4)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          {errors.transferBankAccount && (
            <Typography sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}>
              Select Sending Account for Transfer
            </Typography>
          )}
          <TextField
            type="text"
            name="transferDate"
            label="Set Date to Initiate Transfer"
            required
            fullWidth
            variant="outlined"
            error={errors.transferDate}
            helperText={
              errors.transferDate
                ? "Use YYYY-MM-DD and date cannot be earlier than today"
                : ""
            }
            InputLabelProps={{ shrink: true }}
            value={transferDate}
            onChange={(e) => {
              const normalized = e.target.value
                .trim()
                .replace(/\//g, "-")
                .replace(/[^\d-]/g, "")
                .slice(0, 10);
              setTransferDate(normalized);
              if (errors.transferDate) {
                setErrors((prev) => ({ ...prev, transferDate: false }));
              }
            }}
            placeholder="YYYY-MM-DD"
            inputProps={{
              maxLength: 10,
              pattern: "\\d{4}-\\d{2}-\\d{2}",
              inputMode: "numeric",
            }}
          />
          {/* Input Transfer Amount ($) */}
          <TextField
            type="number"
            name="transferAmount"
            label="Input Transfer Amount ($)"
            required
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            error={errors.transferAmount}
            helperText={
              errors.transferAmount
                ? "Please enter a valid transfer amount"
                : ""
            }
            value={transferAmount}
            onChange={(e) => {
              setTransferAmount(e.target.value);
              if (errors.transferAmount) {
                setErrors((prev) => ({ ...prev, transferAmount: false }));
              }
            }}
          />
          {/* Set a low balance alert when balance is below ， removed it BB2-24, comment 03-28*/}
          {/* <TextField
            type="number"
            name="minDwollaBalance"
            label="Set a low balance alert when balance is below ($)"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            value={minDwollaBalance}
            onChange={(e) => {
              setMinDwollaBalance(e.target.value);
            }}
          /> */}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pt: 2,
            mt: "auto",
            mb: "0px",
            px: "0px",
            py: "0px",
          }}
        >
          <CustomButtonCancel
            label="Cancel"
            onClick={() => {
              props?.cb();
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            size="medium"
            sx={{ width: "120px" }}
            onClick={handleSubmit}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Refill;
